import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getOrdersById, OrderData, updatequantity } from "../../app/services/OrdersService";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";

const tableColumns = [
  "Product Name",
  "Ordered Qty",
  "Updated Qty",
  "Returned",
  "Actual Qty",
  "Price",
  "Tax",
  "After Tax Amount",
];

const GetOrdersData: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [quantities, setQuantities] = useState<{ [index: number]: string }>({});
  const [updatingIndex, setUpdatingIndex] = useState<number | null>(null);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [selectedReturns, setSelectedReturns] = useState<any[]>([]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        if (!id) return;
        const res = await getOrdersById(Number(id));
        if (res.status === "success") {
          setOrderData(res.data);
          const qtyMap = res.data.items.reduce(
            (acc: any, item: any, idx: number) => {
              acc[idx] = String(item.updated_quantity ?? item.quantity);
              return acc;
            },
            {}
          );
          setQuantities(qtyMap);
        } else {
          toast.error("Failed to fetch order");
        }
      } catch (error) {
        toast.error("Failed to fetch order");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleQuantityChange = (index: number, value: string) => {
    if (value === "" || /^[0-9\b]+$/.test(value)) {
      setQuantities((prev) => ({ ...prev, [index]: value }));
    }
  };

  const updateQuantityAPI = async (index: number, productId: number) => {
    if (!orderData) return;
    const qtyStr = quantities[index];
    const qtyNum = parseInt(qtyStr, 10) || 0;

    if (qtyNum <= 0) {
      toast.error("Quantity must be greater than 0");
      return;
    }

    setUpdatingIndex(index);
    try {
      await updatequantity({
        order_id: orderData.id,
        product_id: productId,
        quantity: qtyNum,
      });
      toast.success("Quantity updated");
    } catch (err) {
      toast.error("Failed to update quantity");
    } finally {
      setUpdatingIndex(null);
    }
  };

  const handleQuantityKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    productId: number
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      updateQuantityAPI(index, productId);
    }
  };

  const handleOpenReturnDialog = (productId: number) => {
    if (!orderData?.salesReturns?.items) return;
    const productReturns = orderData.salesReturns.items.filter(
      (sr: any) => sr.product_id === productId
    );
    setSelectedReturns(productReturns);
    setOpenReturnDialog(true);
  };

  const generateInvoicePDF = () => {
    if (!orderData) return;
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Invoice", 14, 20);

    doc.setFontSize(11);
    doc.text(`Order ID: ${orderData.unique_id}`, 14, 30);
    doc.text(`Date: ${new Date(orderData.created_at).toLocaleDateString()}`, 14, 36);
    doc.text(`Customer: ${orderData.user.name}`, 14, 42);
    doc.text(`Email: ${orderData.user.email}`, 14, 48);
    doc.text(`Mobile: ${orderData.user.mobile}`, 14, 54);

    const returnedMap: { [productId: number]: number } = {};
    if (orderData.salesReturns?.items) {
      orderData.salesReturns.items.forEach((srItem: any) => {
        const qty = Number(srItem.quantity) || 0;
        returnedMap[srItem.product_id] =
          (returnedMap[srItem.product_id] || 0) + qty;
      });
    }

    const tableData = orderData.items.map((item, index) => {
      const updatedQuantity = Number(quantities[index]) || 0;
      const returnedQuantity = returnedMap[item.product_id] || 0;
      const actualQty = updatedQuantity > 0 ? updatedQuantity - returnedQuantity : item.quantity - returnedQuantity;
      const total = parseFloat(item.price) * actualQty;
      const taxRate = Number(item.product.tax) || 0;
      const taxAmount = taxRate > 0 ? total * (taxRate / 100) : 0;
      const afterTaxAmount = total + taxAmount;

      return [
        item.product.name,
        item.product.sku,
        item.product.unit,
        item.quantity,
        updatedQuantity,
        returnedQuantity,
        actualQty,
        `₹${item.price}`,
        taxRate > 0 ? `${taxRate}% (₹${taxAmount.toFixed(2)})` : "₹0.00",
        `₹${afterTaxAmount.toFixed(2)}`
      ];
    });

    autoTable(doc, {
      startY: 60,
      head: [tableColumns],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 9 },
    });

    const finalY = (doc as any).lastAutoTable?.finalY || 70;

    const totalPay = orderData.items.reduce((acc, item, index) => {
      const updatedQuantity = Number(quantities[index]) || 0;
      const returnedQuantity = returnedMap[item.product_id] || 0;
      const actualQty = updatedQuantity > 0 ? updatedQuantity - returnedQuantity : item.quantity - returnedQuantity;
      const total = parseFloat(item.price) * actualQty;
      const taxRate = Number(item.product.tax) || 0;
      const taxAmount = taxRate > 0 ? total * (taxRate / 100) : 0;
      return acc + total + taxAmount;
    }, 0);

    doc.text(`Total Pay: ₹${totalPay.toFixed(2)}`, 14, finalY + 10);
    doc.save(`Invoice_${orderData.unique_id}.pdf`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!orderData) return null;

  const returnedMap: { [productId: number]: number } = {};
  if (orderData.salesReturns?.items) {
    orderData.salesReturns.items.forEach((srItem: any) => {
      const qty = Number(srItem.quantity) || 0;
      returnedMap[srItem.product_id] = (returnedMap[srItem.product_id] || 0) + qty;
    });
  }

  const latestStatus = orderData.statusHistory?.[0]?.status?.name?.toLowerCase();
  const isDisabled = latestStatus === "cancelled" || latestStatus === "returned" || latestStatus === "delivered";
  const isDisabledStatus = latestStatus === "cancelled" || latestStatus === "returned" ;

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} mb={2}>Order Details</Typography>

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap">
          <Box flex={1} minWidth="300px" mr={3}>
            <Typography variant="subtitle1"><strong>Order ID:</strong> {orderData.unique_id}</Typography>
            <Typography variant="subtitle1"><strong>Date:</strong> {new Date(orderData.created_at).toLocaleDateString()}</Typography>
            <Typography variant="subtitle1"><strong>Total Amount:</strong> ₹{orderData.total_amount}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Customer Info</Typography>
            <Typography><strong>Name:</strong> {orderData.user.name}</Typography>
            <Typography><strong>Email:</strong> {orderData.user.email}</Typography>
            <Typography><strong>Mobile:</strong> {orderData.user.mobile}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Status History</Typography>
            {orderData.statusHistory.map((status) => (
              <Box key={status.id} mb={1}>
                <Typography><strong>{status.status.name}</strong> - {new Date(status.created_at).toLocaleString()}</Typography>
              </Box>
            ))}
          </Box>

          <Box flex={1} minWidth="300px">
            <Typography><strong>Organisation:</strong> {orderData.org.name}</Typography>
            <Typography><strong>Outlet:</strong> {orderData.outlet.name}</Typography>
            <Typography><strong>Ordered By:</strong> {orderData.user.name}</Typography>
            <Typography><strong>Phone:</strong> {orderData.outlet.mobile}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Shipping Address</Typography>
            <Typography>{orderData.outlet.shipping_addressline1}</Typography>
            <Typography>{orderData.outlet.shipping_addressline2}, {orderData.outlet.shippingcity}</Typography>
            <Typography>Pincode : {orderData.outlet.shippingpincode}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Billing Address</Typography>
            <Typography>{orderData.outlet.billing_addressline1}</Typography>
            <Typography>{orderData.outlet.billing_addressline2}</Typography>
            <Typography>{orderData.outlet.billingcity}</Typography>
            {orderData.outlet.billingpincode && <Typography>Pincode : {orderData.outlet.billingpincode}</Typography>}
          </Box>
        </Box>
      </Paper>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Product Details</Typography>
        <Box>
          <Button variant="text" sx={{ backgroundColor: "#fcb500", color: "#000", fontWeight: 600, mr: 1 }} onClick={generateInvoicePDF}>Invoice</Button>
          <Button variant="text" sx={{ backgroundColor: "#fcb500", color: "#000", fontWeight: 600 }} onClick={() => navigate(`/Orders/sales-return/${orderData.unique_id}`)} disabled={isDisabledStatus}>Sales Return</Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              {tableColumns.map((column) => <TableCell key={column}><strong>{column}</strong></TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {orderData.items.map((item, index) => {
              const updatedQuantity = Number(quantities[index]) || 0;
              const returnedQuantity = returnedMap[item.product_id] || 0;
              const actualQty = updatedQuantity > 0 ? updatedQuantity - returnedQuantity : item.quantity - returnedQuantity;
              const total = parseFloat(item.price) * actualQty;
              const taxRate = Number(item.product.tax) || 0;
              const taxAmount = taxRate > 0 ? total * (taxRate / 100) : 0;
              const afterTaxAmount = total + taxAmount;

              return (
                <TableRow key={index}>
                  <TableCell>{item.product.name}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    <TextField
                        type="number"
                        value={quantities[index]}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        onKeyDown={(e) => handleQuantityKeyDown(e as React.KeyboardEvent<HTMLInputElement>, index, item.product_id)}
                        onBlur={() => updateQuantityAPI(index, item.product_id)}
                        size="small"
                        sx={{ width: 80 }}
                        disabled={isDisabled}
                      />
                  </TableCell>
                  <TableCell>
                    {returnedQuantity > 0 ? <Button variant="text" size="small" onClick={() => handleOpenReturnDialog(item.product_id)}>{returnedQuantity}</Button> : returnedQuantity}
                  </TableCell>
                  <TableCell>{item.final_quantity}</TableCell>
                 <TableCell>
  ₹{parseFloat(item.price).toString()}
</TableCell>
                 <TableCell>
  {parseInt(item.tax_percent) == 0
    ? "0"
    : `${parseFloat(item.tax_percent).toString()}% (₹${item.tax_value})`}
</TableCell>
                  <TableCell>₹{item.total_with_tax.toFixed(2)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* <Paper elevation={2} sx={{ p: 2, maxWidth: 400, ml: "auto" }}>
        <Typography variant="h6" gutterBottom>Bill Details</Typography>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>Total Pay</Typography>
          <Typography fontWeight={600}>₹{orderData.items.reduce((acc, item, index) => {
            const updatedQuantity = Number(quantities[index]) || 0;
            const returnedQuantity = returnedMap[item.product_id] || 0;
            const actualQty = updatedQuantity > 0 ? updatedQuantity - returnedQuantity : item.quantity - returnedQuantity;
            const total = parseFloat(item.price) * actualQty;
            const taxRate = Number(item.product.tax) || 0;
            const taxAmount = taxRate > 0 ? total * (taxRate / 100) : 0;
            return acc + total + taxAmount;
          }, 0).toFixed(2)}</Typography>
          <Typography fontWeight={600}>₹{orderData.total_amount_with_tax}</Typography>
          <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>total tax amount</Typography>
          
            <Typography fontWeight={600}>₹{orderData.total_tax_amount}</Typography>
          
          </Box>
        </Box>
      </Paper> */}

      <Paper elevation={2} sx={{ p: 2, maxWidth: 400, ml: "auto" }}>
  <Typography variant="h6" gutterBottom>
    Bill Details
  </Typography>

  {/* Total Pay Row */}
  <Box display="flex" justifyContent="space-between" mb={1}>    
    <Typography fontWeight={600}>Total Tax Amount</Typography>
    <Typography fontWeight={600}>₹{orderData.total_tax_amount}</Typography>
  </Box>

  {/* Total Tax Amount Row */}
  <Box display="flex" justifyContent="space-between">
    <Typography fontWeight={600}>Total Pay</Typography>
    <Typography fontWeight={600}>₹{orderData.total_amount_with_tax}</Typography>
  </Box>
</Paper>

      <Dialog open={openReturnDialog} onClose={() => setOpenReturnDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Sales Return Details
          <Button onClick={() => setOpenReturnDialog(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReturns.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>SKU</strong></TableCell>
                  <TableCell><strong>Base Price</strong></TableCell>
                  <TableCell><strong>Returned By</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell><strong>Quantity</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedReturns.map((sr, idx) => {
                  const basePrice = parseFloat(sr.product?.base_price) || 0;
                  const qty = Number(sr.quantity) || 0;
                  const amount = basePrice * qty;

                  return (
                    <TableRow key={idx}>
                      <TableCell>{sr.product?.name || "-"}</TableCell>
                      <TableCell>{sr.product?.sku || "-"}</TableCell>
                      <TableCell>₹{basePrice.toFixed(2)}</TableCell>
                      <TableCell>{sr.user?.name || "-"}</TableCell>
                      <TableCell>{sr.reason?.name || "-"}</TableCell>
                      <TableCell>{qty}</TableCell>
                      <TableCell>₹{amount.toFixed(2)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Typography>No return records found</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReturnDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GetOrdersData;
